import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import BrandLogo from './BrandLogo';
import { navigationSections, isItemActive } from './navigation';
import { brand } from '../../theme';

export const SIDEBAR_WIDTH = 264;

function SidebarContent({ pathname, onNavigate, onLogout }) {
    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: brand.ink, color: '#FFFFFF' }}>
            <Box sx={{ px: 2.5, height: 64, display: 'flex', alignItems: 'center', borderBottom: `1px solid ${brand.inkBorder}` }}>
                <BrandLogo tone="dark" />
            </Box>

            <Box component="nav" sx={{ flex: 1, overflowY: 'auto', py: 1.5, px: 1.5 }}>
                {navigationSections.map((section) => (
                    <Box key={section.title} sx={{ mb: 2 }}>
                        <Typography
                            variant="overline"
                            sx={{ display: 'block', px: 1.25, mb: 0.5, color: 'rgba(255,255,255,0.42)' }}
                        >
                            {section.title}
                        </Typography>
                        <List disablePadding>
                            {section.items.map((item) => {
                                const Icon = item.icon;
                                const active = isItemActive(item, pathname);
                                return (
                                    <ListItemButton
                                        key={item.label}
                                        disabled={item.disabled}
                                        selected={active}
                                        onClick={() => item.to && onNavigate(item.to)}
                                        sx={{
                                            position: 'relative',
                                            borderRadius: 1.5,
                                            px: 1.25,
                                            py: 0.75,
                                            mb: 0.25,
                                            color: active ? '#FFFFFF' : 'rgba(255,255,255,0.72)',
                                            '&:hover': { bgcolor: brand.inkSoft, color: '#FFFFFF' },
                                            '&.Mui-selected, &.Mui-selected:hover': { bgcolor: brand.inkSoft },
                                            '&.Mui-selected::before': {
                                                content: '""',
                                                position: 'absolute',
                                                left: -12,
                                                top: 8,
                                                bottom: 8,
                                                width: 3,
                                                borderRadius: '0 3px 3px 0',
                                                bgcolor: brand.green,
                                            },
                                            '&.Mui-disabled': { opacity: 0.45 },
                                        }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 34, color: active ? brand.green : 'inherit' }}>
                                            <Icon sx={{ fontSize: 20 }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={item.label}
                                            secondary={item.hint}
                                            slotProps={{
                                                primary: { sx: { fontSize: '0.8125rem', fontWeight: active ? 600 : 500 } },
                                                secondary: { sx: { fontSize: '0.6875rem', color: 'rgba(255,255,255,0.5)' } },
                                            }}
                                        />
                                    </ListItemButton>
                                );
                            })}
                        </List>
                    </Box>
                ))}
            </Box>

            <Box sx={{ p: 1.5, borderTop: `1px solid ${brand.inkBorder}` }}>
                <ListItemButton
                    onClick={onLogout}
                    sx={{
                        borderRadius: 1.5,
                        px: 1.25,
                        py: 0.75,
                        color: 'rgba(255,255,255,0.72)',
                        '&:hover': { bgcolor: brand.inkSoft, color: '#FFFFFF' },
                    }}
                >
                    <ListItemIcon sx={{ minWidth: 34, color: 'inherit' }}>
                        <LogoutOutlinedIcon sx={{ fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="Sair" slotProps={{ primary: { sx: { fontSize: '0.8125rem', fontWeight: 500 } } }} />
                </ListItemButton>
            </Box>
        </Box>
    );
}

export default function AppShell({ children }) {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
    const [mobileOpen, setMobileOpen] = useState(false);

    useEffect(() => {
        const verificarSessao = async () => {
            const token = localStorage.getItem('token');
            if (!token) {
                navigate('/');
                return;
            }
            try {
                const response = await axios.post(`${process.env.REACT_APP_API_URL}/verificarTokenAdmin`, {}, {
                    headers: { Authorization: token }
                });

                if (response.data.status !== 200) {
                    localStorage.removeItem('token');
                    navigate('/');
                }
            } catch (error) {
                // Sessão inválida (401/403): volta para o login. Falha de rede: mantém o usuário na tela.
                if (error?.response && [401, 403].includes(error.response.status)) {
                    localStorage.removeItem('token');
                    navigate('/');
                } else {
                    console.error('Erro ao verificar sessão:', error);
                }
            }
        };
        verificarSessao();
    }, [navigate]);

    const handleNavigate = (to) => {
        setMobileOpen(false);
        navigate(to);
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        navigate('/');
    };

    const sidebar = <SidebarContent pathname={pathname} onNavigate={handleNavigate} onLogout={handleLogout} />;

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
            {isDesktop ? (
                <Box
                    component="aside"
                    sx={{ width: SIDEBAR_WIDTH, flexShrink: 0, position: 'sticky', top: 0, height: '100vh' }}
                >
                    {sidebar}
                </Box>
            ) : (
                <Drawer
                    open={mobileOpen}
                    onClose={() => setMobileOpen(false)}
                    ModalProps={{ keepMounted: true }}
                    slotProps={{ paper: { sx: { width: SIDEBAR_WIDTH, border: 0 } } }}
                >
                    {sidebar}
                </Drawer>
            )}

            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                {!isDesktop && (
                    <Box
                        component="header"
                        sx={{
                            position: 'sticky',
                            top: 0,
                            zIndex: (t) => t.zIndex.appBar,
                            height: 56,
                            px: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            bgcolor: brand.ink,
                        }}
                    >
                        <IconButton aria-label="Abrir menu" onClick={() => setMobileOpen(true)} sx={{ color: '#FFFFFF' }}>
                            <MenuIcon />
                        </IconButton>
                        <BrandLogo tone="dark" size={24} />
                    </Box>
                )}
                <Box component="main" sx={{ flex: 1, minWidth: 0 }}>
                    {children}
                </Box>
            </Box>
        </Box>
    );
}
